import PolicyLayout, { MetaBox, H2, H3, Ul, SimpleTable } from '../components/PolicyLayout';

const RefundPolicyPage = () => (
  <PolicyLayout
    title="Returns, Refunds & Cancellation Policy"
    subtitle="mybook.rent — India's First Peer-to-Peer Book Rental Platform"
    updated="Last Updated: September 2026"
    testId="refund-page"
  >
    <MetaBox
      rows={[
        ['Applies To', 'All rental transactions and platform interactions on mybook.rent'],
        ['Contact for Queries', 'support@mybook.rent | +91 86607 85749'],
        ['Response Time', 'Within 2 business days of receiving your query'],
      ]}
    />

    <p>
      mybook.rent is a peer-to-peer book rental platform — a marketplace that connects book lenders and borrowers.
      The books listed on our platform belong to individual lenders, not to mybook.rent. This policy governs
      cancellations, returns, and refunds for rental transactions on our platform.
    </p>

    <H2>1. Understanding Our Rental Model</H2>
    <p>
      Before reading this policy, it is important to understand how mybook.rent works — as it is different from a
      standard eCommerce purchase:
    </p>
    <Ul>
      <li>Books on mybook.rent are owned by individual lenders — community members who have listed their personal book collections on the platform.</li>
      <li>When you book a rental, you are borrowing a book from a lender in your neighbourhood and agreeing to return it in the same condition within the agreed rental period.</li>
      <li>mybook.rent acts as the platform facilitating this transaction — we are not the seller or owner of any book listed on the platform.</li>
      <li>Currently, all book pickups and returns are self-pickup — you collect the book directly from the lender&rsquo;s designated location and return it to the same.</li>
      <li>A rental transaction is complete only when the book is collected by the borrower and returned to the lender in acceptable condition.</li>
    </Ul>

    <H2>2. Rental Cancellation Policy</H2>
    <H3>2.1 Cancellation by the Borrower</H3>
    <p>You may cancel a confirmed rental booking subject to the following conditions:</p>
    <SimpleTable
      headers={['When You Cancel', 'Cancellation Allowed?', 'Refund']}
      rows={[
        ['Borrower cancellations until pickup time', 'Yes — cancellation permitted until pickup time', '100% refund to original payment method'],
        ['Lender cancellations', 'Yes — full cancellation permitted', '100% refund to original payment method'],
        ['mybook.rent cancellations', 'Yes — full cancellation permitted', '100% refund to original payment method'],
        ['After pickup time', 'No — cancellation is closed', 'No refund'],
      ]}
    />
    <p>
      To cancel a booking, log into your mybook.rent account, go to &lsquo;My Rentals&rsquo;, and select
      &lsquo;Cancel Booking&rsquo;. Alternatively, contact us at support@mybook.rent or WhatsApp +91 86607 85749
      with your booking reference number.
    </p>

    <H2>3. Book Return Policy</H2>
    <H3>3.1 How Returns Work</H3>
    <p>The following conditions govern returns:</p>
    <Ul>
      <li>Books must be returned by the agreed return date specified at the time of booking.</li>
      <li>Books must be returned in the same condition they were collected — no additional damage, missing pages, writing, highlighting, or stains beyond what was present at pickup.</li>
      <li>Both the lender and borrower are encouraged to do a quick condition check at pickup and return — photographs are recommended.</li>
      <li>The lender must acknowledge receipt of the returned book on the platform within 24 hours of physical return. If the lender does not acknowledge within 24 hours, the return will be considered complete.</li>
    </Ul>

    <H3>3.2 Late Returns</H3>
    <Ul>
      <li>If a book is not returned by the agreed return date, a late fee will apply — calculated at the daily rental rate for each additional day of delay.</li>
      <li>Borrowers are encouraged to request an extension through the platform before the return date if they need more time.</li>
      <li>After 7 days of overdue return with no communication, the book will be treated as lost and the Lost Book Policy (Clause 5) will apply.</li>
    </Ul>

    <H2>4. Damaged Book Policy</H2>
    <H3>4.1 What Counts as Damage</H3>
    <p>
      A book is considered damaged if it is returned with any of the following that were not present at the time of
      pickup:
    </p>
    <Ul>
      <li>Torn, bent, or missing pages</li>
      <li>Water damage, staining, or spillage marks</li>
      <li>Writing, highlighting, or underlining</li>
      <li>Broken spine or cover damage</li>
      <li>Any other physical deterioration beyond normal reading wear</li>
    </Ul>

    <H3>4.2 Borrower&rsquo;s Responsibility for Damage</H3>
    <p>
      If a book is returned in a damaged condition, the borrower is fully responsible for the replacement cost of
      the book. The process is as follows:
    </p>
    <Ul>
      <li>The lender must report the damage through the platform within 24 hours of receiving the returned book, with photographs as evidence.</li>
      <li>mybook.rent will review the evidence and, if the damage is confirmed, notify the borrower of the replacement cost.</li>
      <li>The replacement cost is determined by the current market price of the book (new copy) at the time of the damage report.</li>
      <li>The borrower must pay the replacement cost within 7 days of being notified. Failure to pay may result in account suspension and deduction of money from the user&rsquo;s wallet.</li>
      <li>The damaged book remains with the lender after the replacement cost is paid — it is not returned to mybook.rent.</li>
    </Ul>
    <p>
      <strong>Tip for Borrowers:</strong> We strongly recommend photographing the book&rsquo;s condition at the time
      of pickup as a record. This protects you from any unfair damage claims.
    </p>

    <H2>5. Lost or Unreturned Book Policy</H2>
    <p>
      If a borrowed book is lost, stolen, or not returned within 7 days of the agreed return date without any
      communication or extension request, the following policy applies:
    </p>
    <Ul>
      <li>The borrower will be charged the full replacement cost of the book — based on its current market price (new copy).</li>
      <li>In addition, a penalty fee of ₹100 or 50% of the total rental fee paid (whichever is higher) will be charged to cover the lender&rsquo;s inconvenience and loss of rental income.</li>
      <li>The borrower&rsquo;s account will be suspended until the replacement cost and penalty fee are paid in full.</li>
      <li>Repeated incidents of unreturned books may result in permanent account ban from the platform.</li>
      <li>mybook.rent will make reasonable efforts to facilitate resolution between the lender and borrower before escalating to platform-level penalties.</li>
    </Ul>
    <p>
      mybook.rent is a community platform built on trust. We take the protection of our lenders&rsquo; property very
      seriously. Please treat every borrowed book as you would a treasured possession — because it is someone
      else&rsquo;s.
    </p>

    <H2>6. Refund Process</H2>
    <H3>6.1 How Refunds Are Processed</H3>
    <Ul>
      <li>All approved refunds are processed to the original payment method used at the time of booking.</li>
      <li>Refund processing time is 3–4 business days from the date of refund approval by mybook.rent.</li>
      <li>Bank processing times may add an additional 2–5 business days depending on your bank or payment provider.</li>
      <li>You will receive an email confirmation from support@mybook.rent once your refund has been initiated.</li>
    </Ul>

    <H3>6.2 Refund Summary — Quick Reference</H3>
    <SimpleTable
      headers={['Scenario', 'Condition', 'Refund']}
      rows={[
        ['Borrower cancels before pickup (&gt;24 hrs)', 'More than 24 hrs before pickup', '100% Refund'],
        ['Lender cancels confirmed booking', 'Any time', '100% Refund'],
        ['mybook.rent cancels booking', 'Any time', '100% Refund'],
        ['Borrower cancels within 24 hrs', 'Within 24 hrs of pickup time', 'No Refund'],
        ['Book returned damaged', 'Damage confirmed by platform', 'No Refund + Replacement Cost'],
        ['Book lost / not returned', '7+ days overdue, no communication', 'No Refund + Replacement + Penalty'],
      ]}
    />

    <H2>7. Disputes Between Lenders &amp; Borrowers</H2>
    <p>
      As a peer-to-peer platform, mybook.rent facilitates transactions between community members. In the event of a
      dispute between a lender and borrower regarding book condition, return, or damage:
    </p>
    <Ul>
      <li>Either party may raise a dispute through the platform&rsquo;s support channel at support@mybook.rent within 48 hours of the event in question.</li>
      <li>mybook.rent will review the available evidence (photographs, booking records, communication logs) and provide a resolution within 5 business days.</li>
      <li>mybook.rent&rsquo;s decision in disputes shall be final and binding on both parties.</li>
      <li>mybook.rent is not liable for the quality, condition, or accuracy of any book listing — these are the responsibility of the individual lender.</li>
    </Ul>

    <H2>8. Contact Us</H2>
    <p>For any questions, concerns, or requests related to this policy, please reach out to us:</p>
    <MetaBox
      rows={[
        ['Email', 'support@mybook.rent'],
        ['WhatsApp / Phone', '+91 86607 85749'],
        ['Response Time', 'Within 2 business days'],
        ['Registered Address', '312, Incor Opulence, Gunjur Palya Road, Bengaluru, Karnataka – 560087'],
        ['Platform', 'mybook.rent | BOOKONRENT'],
      ]}
    />
    <p>
      We are a community built on trust, respect, and a shared love for books. Our policies are designed to protect
      every member of that community — lenders and borrowers equally. When in doubt, please reach out to us before
      raising a dispute. We are always here to help.
    </p>
  </PolicyLayout>
);

export default RefundPolicyPage;
